<template>
  <div class="dashboard-shell">
    <header class="dashboard-header">
      <div class="dashboard-title">
        <div class="title-icon" aria-hidden="true">
          <i class="fa-duotone fa-solid fa-grid-2"></i>
        </div>

        <div class="title-text">
          <div class="title-select-wrap">
            <select class="title-select" :value="selectedDashboard" @change="onDashboardChange">
              <option value="loanOfficer">Originate</option>
              <option value="processor">Process</option>
              <option v-if="isCorp" value="conditionOnly">Underwrite</option>
            </select>
          </div>

          <div class="subtitle">
            {{ subtitle }}
          </div>
        </div>
      </div>

      <!-- Chat button lives in the header so it's always accessible
      <button class="chat-toggle-btn" @click="isDrawerOpen = true" aria-label="Open chat">
        <i class="fa-duotone fa-solid fa-rocket"></i>
        Chat
        <span v-if="unreadCount" class="chat-badge">{{ unreadCount }}</span>
      </button> -->
    </header>

    <main class="dashboard-content">
      <LoanOfficerDashboard v-if="selectedDashboard === 'loanOfficer'" />
      <LoanOfficerAssistantDashboard v-else-if="selectedDashboard === 'loa'" />
      <ProcessorDashboard v-else-if="selectedDashboard === 'processor'" />
      <ConditionOnlyDashboard v-else-if="selectedDashboard === 'conditionOnly'" />
    </main>

    <!-- Drawer overlay — only rendered when isDrawerOpen is true -->
    <transition name="drawer">
      <div v-if="isDrawerOpen" class="drawer-overlay" @click.self="isDrawerOpen = false">
        <div class="drawer-panel" role="dialog" aria-label="LoanLaunch Chat">
          <LoanLaunchMessagesDrawer @close="isDrawerOpen = false" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import LoanOfficerDashboard from "../components/commandCenter/LoanOfficerDashboard.vue"
import LoanOfficerAssistantDashboard from "../components/commandCenter/LoanOfficerAssistantDashboard.vue"
import ProcessorDashboard from "../components/commandCenter/ProcessorDashboard.vue"
import ConditionOnlyDashboard from "./ConditionOnlyDashboard.vue"
import LoanLaunchMessagesDrawer from "@/components/commandCenter/LoanLaunchMessagesDrawer.vue"

const DASH_FLAG = "dashboards_selected"

function rolesInclude(roles, role) {
  const target = String(role || "").toLowerCase().trim()
  return (roles || []).map(x => String(x || "").toLowerCase().trim()).includes(target)
}

function getRolesFromStorage(key) {
  const raw = localStorage.getItem(key)
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw)

    if (Array.isArray(parsed)) {
      return parsed.map(x => String(x || "").trim()).filter(Boolean)
    }

    return String(parsed || "")
      .split(",")
      .map(x => String(x || "").trim())
      .filter(Boolean)
  } catch {
    return String(raw || "")
      .split(",")
      .map(x => String(x || "").trim())
      .filter(Boolean)
  }
}

export default {
  name: "DashboardShell",

  components: {
    LoanOfficerDashboard,
    LoanOfficerAssistantDashboard,
    ProcessorDashboard,
    ConditionOnlyDashboard,
    LoanLaunchMessagesDrawer
  },

  data() {
    const activeRoles = getRolesFromStorage("active_user_roles")
    const isCorp = rolesInclude(activeRoles, "Corp")

    return {
      isCorp,
      selectedDashboard: localStorage.getItem(DASH_FLAG) || "loanOfficer",
      isDrawerOpen: false,
      // wire this to your real unread count from props/store if available
      unreadCount: 0
    }
  },

  computed: {
    subtitle() {
      if (this.selectedDashboard === "loanOfficer") {
        return "Your Pipeline, Simplified"
      }

      if (this.selectedDashboard === "loa") {
        return "View the loan officer assistant dashboard."
      }

      if (this.selectedDashboard === "processor") {
        return "Pipeline Progress, Simplified"
      }

      if (this.selectedDashboard === "conditionOnly") {
        return "Smart Decisions. Strong Loans"
      }

      return "Pick which dashboard you want to land on."
    }
  },

  methods: {
    onDashboardChange(e) {
      const val = e.target.value
      this.selectedDashboard = val
      localStorage.setItem(DASH_FLAG, val)
    }
  }
}
</script>

<style scoped>
.dashboard-shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  min-height: 0;
  padding: 10px;
}

.dashboard-header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-bottom: 10px;
  background: #fff;
}

.dashboard-content {
  flex: 1 1 auto;
  display: flex;
  gap: 14px;
  min-height: 0;
  overflow: auto;
}

.dashboard-title {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  align-items: stretch;
  column-gap: 14px;
  min-width: 0;
}

.title-icon {
  width: 48px;
  border-radius: 12px;
  background: #0c2d5a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(12, 45, 90, 0.18);
}

.title-icon i {
  font-size: 22px;
  line-height: 1;
}

.title-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.title-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
}

.title-select {
  height: 38px;
  min-width: 320px;
  max-width: 100%;
  padding: 0 36px 0 0;
  border: 0;
  background: transparent;
  color: #0b1f3a;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 2.2rem;
  line-height: 1.1;
  font-weight: 500;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

.title-select-wrap::after {
  content: "";
  position: absolute;
  right: 8px;
  top: 50%;
  width: 10px;
  height: 10px;
  border-right: 2px solid #0b1f3a;
  border-bottom: 2px solid #0b1f3a;
  transform: translateY(-65%) rotate(45deg);
  pointer-events: none;
}

.title-select:focus {
  opacity: 0.9;
  padding-left: 10px;
}

.subtitle {
  font-size: 12px;
  line-height: 1.25;
  color: #5f6f84;
  max-width: 520px;
}

/* ── Chat toggle button ─────────────────────────────────────── */
.chat-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #0b1f3a;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}

.chat-toggle-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.chat-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e53e3e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

/* ── Drawer overlay + panel ─────────────────────────────────── */
.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.drawer-panel {
  width: 420px;
  max-width: 100vw;
  height: 100%;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ── Fly-in / fly-out transition ────────────────────────────── */
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.22s ease;
}

.drawer-enter-active .drawer-panel,
.drawer-leave-active .drawer-panel {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
}

.drawer-enter-from .drawer-panel,
.drawer-leave-to .drawer-panel {
  transform: translateX(100%);
}

@media (max-width: 780px) {
  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 10px 12px;
  }

  .dashboard-title {
    grid-template-columns: 42px minmax(0, 1fr);
    column-gap: 12px;
  }

  .title-icon {
    width: 42px;
    min-height: 56px;
    border-radius: 10px;
  }

  .title-icon i {
    font-size: 20px;
  }

  .title-select {
    min-width: 0;
    width: 100%;
    font-size: 1.6rem;
    height: 34px;
  }

  .title-select-wrap {
    width: 100%;
  }

  .subtitle {
    max-width: 100%;
    font-size: 12px;
  }

  .drawer-panel {
    width: 100vw;
  }
}
</style>